<template>
  <div class="content-page">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderContent :page="currentPageInfo" />
    <div class="post-container">
      <BrandArticle :page="article" />
    </div>
    <hr
      v-if="actualities.length"
      class="separator"
    />
    <section
      v-if="actualities.length"
      class="other section"
    >
      <div class="title-line line">
        <h2 class="title-strat">Les autres actualités PlayStation</h2>
      </div>
      <div class="slider">
        <ActuPlaystationSlider :actus="actualities" />
      </div>
    </section>
    <DoNotMissSection
      v-if="slidersStore.doNotMiss.length"
      :slider-items="slidersStore.doNotMiss"
    />
    <p class="mention">
      * Récompense provisoire sous réserve de maintenir votre place au
      classement jusqu'à la fin du mois.
    </p>
  </div>
</template>

<script setup lang="ts">
import type { FormattedArticle } from '~/types'

definePageMeta({
  middleware: async (to) => {
    const loadActuPlay = useLoadActuPlay()
    return loadActuPlay(to)
  },
})

const actualitiesWapStore = useActualitiesWAPStore()
const { actuality, actualities } = storeToRefs(actualitiesWapStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les actualités PlayStation',
      route: '/actualites-playstation',
    },
    {
      title: actuality.value?.title,
      route: '',
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: actuality.value?.title_seo,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: actuality.value?.title_seo,
  description: actuality.value?.description_seo,
  keywords: actuality.value?.keywords_seo,
  ogType: 'article',
  ogTitle: actuality.value?.title_og,
  ogDescription: actuality.value?.description_og,
  ogImage: actuality.value?.image_og
    ? actuality.value?.image_og.url
    : 'https://www.weareplaystation.fr/img/og-img.jpg',
})

const route = useRoute()
const slidersStore = useDoNotMissStore()

const slug = computed(() => {
  return route.params.title as string
})

const { authenticated } = useAuthenticated()

const currentPageInfo = computed(() => {
  const rawActuality = unref(actuality)
  const { image, imageMobile } = getImages(actuality)

  return {
    title: rawActuality?.title,
    image,
    imageMobile,
    supTitle: 'breaking news',
  }
})

const socialStore = useSocialStore()

const article = computed(() => {
  const rawActuality = unref(actuality)
  return {
    name: rawActuality?.title,
    date: rawActuality?.created_at,
    title: rawActuality?.description,
    content1: withImageAlt(rawActuality?.content1, rawActuality?.title),
    fullImage: rawActuality?.image_content,
    content2: withImageAlt(rawActuality?.content2, rawActuality?.title),
    cta1: {
      text: rawActuality?.focus_title1,
      route: rawActuality?.focus_url1
        ? rawActuality?.focus_url1
        : rawActuality?.focus_mail_to1,
      labelButton: rawActuality?.focus_button_title1,
    },
    cta2: {
      text: rawActuality?.focus_title2,
      route: rawActuality?.focus_url2
        ? rawActuality?.focus_url2
        : rawActuality?.focus_mail_to2,
      labelButton: rawActuality?.focus_button_title2,
    },
    sideContent: {
      card: rawActuality?.article_linked
        ? {
            ...rawActuality?.article_linked,
            route: `/actualites-playstation/${rawActuality?.article_linked.slug}`,
          }
        : undefined,
      community: rawActuality?.community_linked
        ? {
            ...rawActuality?.community_linked,
            route: `/communautes/${rawActuality?.community_linked.slug}`,
          }
        : undefined,
    },
    comments: socialStore.social.comments,
    commentsCount: socialStore.social.comments_count,
    has_more_comments: socialStore.social.has_more_comments,
    likes: socialStore.social.likes_count,
    shares: socialStore.social.shares_count,
    hide_comments: rawActuality?.hide_comments,
    is_liked: socialStore.social.is_liked,
    slug: slug.value,
    type: 'actu-play',
  } satisfies FormattedArticle
})

const loadActuPlay = useLoadActuPlay()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadActuPlay(route)
  }
})
</script>

<style lang="scss" scoped>
.content-page {
  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: $white;
      border-radius: 4.2rem;

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }
    }
  }

  hr {
    height: 1px;
    margin: 8rem 8rem 0;
    background-color: rgba($black, 0.2);
    border: 0;
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 1024px) {
  .content-page {
    hr.separator {
      display: none;
    }
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
